import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { fetchAllInteraction } from "../../services/InteractionAndOffer";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "chartjs-adapter-date-fns";
import { Range } from "react-range";
import Spinner from "../shared/Spinner";

// Register Chart.js + plugins
Chart.register(...registerables, ChartDataLabels);

function TotalViewForUserPost() {
  //  ➤ view mode: daily or monthly
  const [viewMode, setViewMode] = useState("daily"); // "daily" | "monthly"

  //  ➤ daily series
  const [dailyDates, setDailyDates] = useState([]);
  const [dailyCounts, setDailyCounts] = useState([]);
  const [dailyRange, setDailyRange] = useState([0, 0]);

  //  ➤ monthly series
  const [monthlyDates, setMonthlyDates] = useState([]);
  const [monthlyCounts, setMonthlyCounts] = useState([]);
  const [monthlyRange, setMonthlyRange] = useState([0, 0]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 1️⃣ fetch & build both daily and monthly tallies
  useEffect(() => {
    async function loadData() {
      try {
        const data = await fetchAllInteraction();
        // only user-posted interactions
        const userData = data.filter(
          (i) => i.offer?.poster?.toLowerCase() === "user"
        );

        // DAILY tally per ISO date
        const dayTally = {};
        userData.forEach((i) => {
          const date = new Date(i.createdAt || i.offer.createdAt)
            .toISOString()
            .split("T")[0];
          dayTally[date] = (dayTally[date] || 0) + 1;
        });
        const dDates = Object.keys(dayTally).sort(
          (a, b) => new Date(a) - new Date(b)
        );
        const dCounts = dDates.map((d) => dayTally[d]);
        setDailyDates(dDates);
        setDailyCounts(dCounts);
        if (dDates.length > 1) setDailyRange([0, dDates.length - 1]);

        // MONTHLY tally by "YYYY-MM"
        const monthTally = {};
        userData.forEach((i) => {
          const dt = new Date(i.createdAt || i.offer.createdAt);
          const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
            2,
            "0"
          )}`;
          monthTally[key] = (monthTally[key] || 0) + 1;
        });
        const mDates = Object.keys(monthTally).sort(
          (a, b) => new Date(a + "-01") - new Date(b + "-01")
        );
        const mCounts = mDates.map((m) => monthTally[m]);
        setMonthlyDates(mDates);
        setMonthlyCounts(mCounts);
        if (mDates.length > 1) setMonthlyRange([0, mDates.length - 1]);
      } catch (err) {
        console.error(err);
        setError("Error fetching interactions");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="relative min-h-[calc(100vh-8rem)]">
        <Spinner size="xl" fullScreen={true} />
      </div>
    );
  }
  if (error) return <p className="text-red-500 text-center p-4">{error}</p>;

  // 2️⃣ pick the correct series & slice range
  const [start, end] = viewMode === "daily" ? dailyRange : monthlyRange;
  const labels = (viewMode === "daily" ? dailyDates : monthlyDates).slice(
    start,
    end + 1
  );
  const counts = (viewMode === "daily" ? dailyCounts : monthlyCounts).slice(
    start,
    end + 1
  );

  // 3️⃣ build chart data & options
  const chartData = {
    labels,
    datasets: [
      {
        label:
          viewMode === "daily"
            ? "Total User Interactions (Daily)"
            : "Total User Interactions (Monthly)",
        data: counts,
        fill: false,
        borderColor: "blue",
        tension: 0.4,
        pointBackgroundColor: "blue",
        datalabels: {
          anchor: "end",
          align: "top",
          font: { weight: "bold" },
          offset: 4,
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        time: {
          unit: viewMode === "daily" ? "day" : "month",
          tooltipFormat: viewMode === "daily" ? "MMMM d, yyyy" : "MMMM yyyy",
          displayFormats: {
            day: "MMM d, yyyy",
            month: "MMM yyyy",
          },
        },
        ticks: { autoSkip: true, maxRotation: 45, minRotation: 0 },
      },
      y: { beginAtZero: true },
    },
    plugins: {
      datalabels: { display: true },
      legend: { display: true },
    },
  };

  return (
    <div className="p-6 bg-white shadow-lg">
      <h2 className="mb-4">Total User Interactions Over Time</h2>

      {/* 📊 View selector */}
      <div className="mb-4">
        <label className="font-medium mr-2">View:</label>
        <select
          value={viewMode}
          onChange={(e) => setViewMode(e.target.value)}
          className="border rounded px-2 py-1"
        >
          <option value="daily">Daily</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      {/* Chart */}
      <div style={{ height: "300px", position: "relative" }}>
        <Line data={chartData} options={options} />
      </div>

      {/* Slider */}
      {(viewMode === "daily" ? dailyDates.length : monthlyDates.length) > 1 && (
        <div className="mt-6">
          <Range
            values={viewMode === "daily" ? dailyRange : monthlyRange}
            step={1}
            min={0}
            max={(viewMode === "daily" ? dailyDates : monthlyDates).length - 1}
            onChange={(vals) =>
              viewMode === "daily" ? setDailyRange(vals) : setMonthlyRange(vals)
            }
            renderTrack={({ props, children }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: 8,
                  width: "100%",
                  backgroundColor: "#ddd",
                  borderRadius: 8,
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    height: "100%",
                    backgroundColor: "#4A90E2",
                    borderRadius: 8,
                    left: `${
                      (start /
                        ((viewMode === "daily" ? dailyDates : monthlyDates)
                          .length -
                          1)) *
                      100
                    }%`,
                    width: `${
                      ((end - start) /
                        ((viewMode === "daily" ? dailyDates : monthlyDates)
                          .length -
                          1)) *
                      100
                    }%`,
                  }}
                />
                {children}
              </div>
            )}
            renderThumb={({ props, index }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: 20,
                  width: 20,
                  backgroundColor: "#4A90E2",
                  borderRadius: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: -28,
                    fontSize: 12,
                    color: "#333",
                  }}
                >
                  {
                    (viewMode === "daily" ? dailyDates : monthlyDates)[
                      viewMode === "daily"
                        ? dailyRange[index]
                        : monthlyRange[index]
                    ]
                  }
                </div>
              </div>
            )}
          />

          <div className="mt-2 font-medium">
            Showing from <strong>{labels[0]}</strong> to{" "}
            <strong>{labels[labels.length - 1]}</strong>
          </div>
        </div>
      )}
    </div>
  );
}

export default TotalViewForUserPost;
